import { FC, useState } from 'react'

interface UsageInstructionsProps {
  activeTab: 'available' | 'queue' | 'inservice' | 'history' | 'conference' | 'reasons' | 'config'
  isPrivileged?: boolean
}

export const UsageInstructions: FC<UsageInstructionsProps> = ({ activeTab, isPrivileged = false }) => {
  const [isOpen, setIsOpen] = useState(false)

  const instructions: Record<string, { title: string; content: string[] }> = {
    available: {
      title: 'Vendedores Disponíveis',
      content: [
        'Nesta aba você visualiza todos os vendedores disponíveis que não estão na fila.',
        'Use o campo de pesquisa para encontrar rapidamente um vendedor pelo ID ou nome.',
        'Clique no botão "Adicionar à Fila" para inserir um vendedor na fila de atendimento.',
        'O vendedor será automaticamente posicionado no final da fila.',
        'Você pode filtrar por status ou ordenar a lista conforme necessário.',
      ],
    },
    queue: {
      title: 'Fila Atual',
      content: [
        'Visualiza a ordem atual de atendimento dos vendedores.',
        'O card "Próximo na Fila" mostra qual vendedor será atendido em seguida.',
        'Arraste e solte: Clique e arraste qualquer linha para reordenar a fila (coloque acima ou abaixo de qualquer posição).',
        'Use as setas ⬆️ ⬇️ para mover vendedores para cima ou para baixo (método alternativo).',
        'Botão (Chamar): Marca que o vendedor foi chamado.',
        'Botão 🗑️ (Remover): Remove permanentemente o vendedor da fila.',
        'A posição é recalculada automaticamente após cada movimento (1º, 2º, 3º...).',
        'Um alerta sonoro toca quando você move um vendedor com sucesso.',
        'Dica: O cursor muda para indicar que você pode arrastar a linha ⋮⋮',
        isPrivileged
          ? 'Perfil gerente/admin: ações sensíveis exigem confirmação de senha/PIN com permissão válida.'
          : 'Perfil vendedor: você pode acompanhar a fila, mas não pode reordenar, pausar ou remover vendedores.',
      ],
    },
    inservice: {
      title: 'Em Atendimento',
      content: [
        'Mostra os vendedores que estão sendo atendidos no momento.',
        'Exibe informações como duração do atendimento e status.',
        'Use o botão "Finalizar" para concluir o atendimento e registrar o resultado.',
        'Você pode selecionar motivos de venda ou não venda durante o atendimento.',
        'O sistema registra automaticamente o horário de início e fim.',
      ],
    },
    history: {
      title: 'Histórico',
      content: [
        'Visualiza o histórico completo de atendimentos realizados.',
        'Filtre por data, vendedor, resultado ou duração do atendimento.',
        'Cada registro mostra detalhes da sessão: hora, duração, resultado final.',
        'Use para análise de performance e produtividade dos vendedores.',
        'Todos os atendimentos ficam registrados para auditoria e relatórios.',
      ],
    },
    conference: {
      title: 'Conferência',
      content: [
        'Use Data Início e Data Final para limitar o período da conferência de pedidos.',
        'Se a loja tiver integração TOTVS detectada, o campo Origem aparece para alternar entre Híbrido, Apenas Ninegrid e Apenas TOTVS.',
        'Se não houver integração TOTVS, a origem fica fixa em Ninegrid.',
        'Híbrido consulta Ninegrid + TOTVS na mesma pesquisa.',
        'Apenas Ninegrid pesquisa somente o banco local da plataforma.',
        'Apenas TOTVS pesquisa somente a integração TOTVS (com paginação por página).',
        'O campo Pedido / Pedido Externo é único: em Híbrido e Ninegrid ele aceita pedido ou código externo; em TOTVS ele precisa ser numérico.',
        'O campo Vendedor carrega usuários do tipo seller da loja.',
        'Sem vendedor selecionado, a consulta exige senha de admin, gerente ou owner.',
        'Com vendedor selecionado, a consulta aceita senha de admin, gerente, owner ou do vendedor selecionado.',
        'A listagem mostra pedidos com evidência de vendedor no conteúdo (CPF/Código).',
      ],
    },
    reasons: {
      title: 'Motivos de Não Venda',
      content: [
        'Estatísticas dos motivos pelos quais não houve venda.',
        'Exibe gráficos com os principais motivos de não venda.',
        'Ajuda a identificar padrões e áreas de melhoria.',
        'Os dados são agregados por período (hoje, semana, mês).',
        'Use para análise e treinamento dos vendedores.',
      ],
    },
    config: {
      title: 'Configuração',
      content: [
        'Gerencie as configurações de notificação para toda a fila de atendimento.',
        'Email: Ative para enviar notificações por email quando vendedores mudarem de posição na fila.',
        'Push: Ative para enviar notificações push quando a posição na fila for alterada.',
        'WhatsApp: Ative para notificar vendedores via WhatsApp sobre mudanças na fila.',
        'SMS: Ative para enviar notificações via SMS quando vendedores mudarem de posição na fila.',
        'Link do Painel: Configure o URL do painel de acompanhamento que será compartilhado com os vendedores.',
        'Clique em "Salvar Configurações" para aplicar as mudanças.',
        'Use "Recarregar" para atualizar as configurações do servidor sem perder alterações.',
        'Use "Deletar Tudo" para remover todas as configurações (use com cuidado).',
        'As configurações são específicas do seu espaço de plugins e afetam todos os vendedores.',
      ],
    },
  }

  const current = instructions[activeTab]

  return (
    <div className='accordion mb-4'>
      <div className='accordion-item'>
        <h2 className='accordion-header'>
          <button
            className='accordion-button collapsed'
            type='button'
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            style={{
              backgroundColor: isOpen ? '#f0f6ff' : 'transparent',
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <i className='fas fa-lightbulb me-2 text-warning'></i>
            <strong>Saiba mais...</strong>
            <span className='ms-2 text-muted'>({current.title})</span>
          </button>
        </h2>
        {isOpen && (
          <div className='accordion-body bg-light-info p-4' style={{ borderTop: '1px solid #e0e0e0' }}>
            <div className='mb-3'>
              <h6 className='fw-bold text-info mb-3'>
                <i className='fas fa-info-circle me-2'></i>
                Como usar: {current.title}
              </h6>
              <ul className='list-unstyled'>
                {current.content.map((item, index) => (
                  <li key={index} className='mb-2 d-flex'>
                    <i className='fas fa-check-circle text-success me-3 mt-1' style={{ minWidth: '20px' }}></i>
                    <span className='text-muted'>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className='alert alert-light-primary mb-0'
              style={{ borderLeft: '4px solid #3699ff' }}
            >
              <i className='fas fa-star me-2 text-primary'></i>
              <strong>Dica:</strong> Clique em diferentes abas para ver instruções específicas de cada funcionalidade.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
